import { apiService } from './apiService';

export interface BootstrapRole {
  id: number;
  name: string;
  description?: string;
}

/** Minimal shapes returned by GET /bootstrap/lookups (avoids circular imports). */
export interface BootstrapCategory {
  id: number;
  name: string;
  description?: string;
  parent_id?: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface BootstrapLocation {
  id: number;
  name: string;
  code?: string;
  address?: string;
  is_active: boolean;
  location_type?: string;
  is_warehouse?: boolean;
}

export interface BootstrapVendor {
  id: number;
  name: string;
  code?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  is_active: boolean;
}

export interface BootstrapLookups {
  categories: BootstrapCategory[];
  locations: BootstrapLocation[];
  vendors: BootstrapVendor[];
  roles: BootstrapRole[];
}
const STORAGE_KEY = 'stockm_bootstrap_lookups_v1';
const DEFAULT_TTL_MS = 5 * 60 * 1000; // match backend LOOKUP_CACHE_TTL

type StoredLookups = { fetchedAt: number; data: BootstrapLookups };

let inflight: Promise<BootstrapLookups> | null = null;
let memoryCache: StoredLookups | null = null;

function readSession(): StoredLookups | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredLookups;
    if (!parsed?.data || !parsed.fetchedAt) return null;
    if (Date.now() - parsed.fetchedAt > DEFAULT_TTL_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(data: BootstrapLookups): void {
  const entry: StoredLookups = { fetchedAt: Date.now(), data };
  memoryCache = entry;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // quota or private mode — memory cache only
  }
}

/** Clear cached lookups (call after category/location/vendor CRUD). */
export function invalidateBootstrapLookups(): void {
  memoryCache = null;
  inflight = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Single API call for categories, locations, vendors, roles (permission-filtered).
 * Dedupes concurrent requests; caches in sessionStorage for 5 minutes.
 */
export async function fetchBootstrapLookups(force = false): Promise<BootstrapLookups> {
  if (!force) {
    if (memoryCache && Date.now() - memoryCache.fetchedAt <= DEFAULT_TTL_MS) {
      return memoryCache.data;
    }
    const fromSession = readSession();
    if (fromSession) {
      memoryCache = fromSession;
      return fromSession.data;
    }
  }

  if (!inflight) {
    inflight = apiService
      .get<BootstrapLookups>('/bootstrap/lookups')
      .then((data) => {
        const normalized: BootstrapLookups = {
          categories: Array.isArray(data?.categories) ? data.categories : [],
          locations: Array.isArray(data?.locations) ? data.locations : [],
          vendors: Array.isArray(data?.vendors) ? data.vendors : [],
          roles: Array.isArray(data?.roles) ? data.roles : [],
        };
        writeSession(normalized);
        return normalized;
      })
      .finally(() => {
        inflight = null;
      });
  }

  return inflight;
}

/** Warm lookups after login (non-blocking). */
export function prefetchBootstrapLookups(): void {
  void fetchBootstrapLookups().catch(() => {
    /* optional — pages fall back to individual APIs */
  });
}
